import React from "react"
import toast from "react-hot-toast"
import { useApp } from "../context/AppContext"
import { Icon, useIsMobile } from "../shared"
import { useOrgName } from "../shared/BrandLogo"
import { Card, Badge, Btn } from "../shared/components"
import { Modal, ConfirmModal, inputStyle, labelStyle } from "../shared/Modal"
import { DocUploader, FileDropzone, UploadProgress, useUpload, fmtBytes, isCanceled } from "../shared/FileUpload"
import {
  listCourses,
  getCourse,
  createCourse,
  updateCourse,
  deleteCourse,
  createModule,
  updateModule,
  deleteModule,
  createLesson,
  updateLesson,
  deleteLesson,
  uploadModuleDoc,
  deleteModuleDoc,
  uploadCourseDoc,
  renameCourseDoc,
  deleteCourseDoc,
  getCourseDocDownloadUrl,
  markLessonCompleteForAll,
  uploadLessonDoc,
  deleteLessonDoc,
  getLessonDocUrl,
  getModuleDocDownloadUrl,
  reorderModules,
} from "../api/courses.api"
import { uploadVideoForLesson, deleteVideo } from "../api/videos.api"
import {
  getGameScenarioAdmin,
  createGameFromTemplate,
  upsertGameScenario,
  updateGameScenario,
  uploadGameBuild,
} from "../api/games.api"
import { tintBg, tintFg } from '../lib/tint'
import { modLabel } from '../lib/modLabel'

/* ============================================================
   Course Manage — Course/Module/Lesson authoring (trainer/admin).
   Companion to the read-only CourseDetailScreen viewer.
   Reuses existing video-upload + game authoring APIs.
   ============================================================ */

// Batas yang sama dengan server (course routes): tipe berkas, dan 25 MB per dokumen
// pelajaran/modul. Dokumen referensi kursus boleh sampai 100 MB: buku panduan jauh
// lebih besar daripada slide.
const LESSON_DOC_TYPES = ["pdf"]
const MODULE_DOC_TYPES = ["pdf", "doc", "docx", "ppt", "pptx", "xls", "xlsx"]
const DOC_MAX_BYTES = 25 * 1024 * 1024
const COURSE_DOC_MAX_BYTES = 100 * 1024 * 1024

const STATUS_TONE = { DRAFT: "default", PUBLISHED: "success", ARCHIVED: "warning" }
function statusLabel(s, lang) {
  if (lang !== "id") return s.charAt(0) + s.slice(1).toLowerCase()
  return { DRAFT: "Draf", PUBLISHED: "Terbit", ARCHIVED: "Arsip" }[s] || s
}
const LESSON_META = {
  VIDEO: { icon: "play", color: "var(--program-color)", soft: "var(--program-color-soft)" },
  PDF: { icon: "doc", color: tintFg("#B53A2C"), soft: tintBg("#B53A2C") },
  GAME: { icon: "compass", color: tintFg("#2E7D5B"), soft: tintBg("#2E7D5B") },
}
function lessonTypeLabel(type, lang) {
  if (type === "VIDEO") return "Video"
  if (type === "PDF") return lang === "id" ? "Dokumen" : "Document"
  if (type === "GAME") return lang === "id" ? "Simulasi" : "Simulation"
  return type
}


// ── Course create/edit modal ──────────────────────────────────
function CourseFormModal({ programId, course, onClose, onSaved }) {
  const { lang } = useApp()
  const isEdit = !!course
  const [title, setTitle] = React.useState(course?.title || "")
  const [description, setDescription] = React.useState(course?.description || "")
  const [status, setStatus] = React.useState(course?.status || "DRAFT")
  const [quota, setQuota] = React.useState(course?.quota ?? "")
  const [busy, setBusy] = React.useState(false)
  const [err, setErr] = React.useState(null)

  async function submit(e) {
    e.preventDefault()
    setBusy(true); setErr(null)
    try {
      const payload = {
        title: title.trim(),
        description: description.trim(),
        status,
        quota: quota === "" ? null : Number(quota),
      }
      if (isEdit) await updateCourse(programId, course.id, payload)
      else await createCourse(programId, payload)
      toast.success(lang === "id" ? "Kursus disimpan" : "Course saved")
      onSaved()
    } catch (ex) {
      setErr(ex?.response?.data?.error || ex.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal title={isEdit ? (lang === "id" ? "Edit Kursus" : "Edit Course") : (lang === "id" ? "Kursus Baru" : "New Course")} onClose={onClose}>
      <form onSubmit={submit} className="col gap-3">
        {err && <div className="banner red"><Icon name="alert" size={14} />{err}</div>}
        <label className="col gap-1">
          <span style={labelStyle}>{lang === "id" ? "Judul" : "Title"}</span>
          <input style={inputStyle} value={title} onChange={(e) => setTitle(e.target.value)} required autoFocus />
        </label>
        <label className="col gap-1">
          <span style={labelStyle}>{lang === "id" ? "Deskripsi" : "Description"}</span>
          <textarea style={{ ...inputStyle, height: 80, padding: 10 }} value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>
        <div className="row gap-3">
          <label className="col gap-1" style={{ flex: 1 }}>
            <span style={labelStyle}>{lang === "id" ? "Status" : "Status"}</span>
            <select style={inputStyle} value={status} onChange={(e) => setStatus(e.target.value)}>
              {["DRAFT", "PUBLISHED", "ARCHIVED"].map((s) => <option key={s} value={s}>{statusLabel(s, lang)}</option>)}
            </select>
          </label>
          <label className="col gap-1" style={{ flex: 1 }}>
            <span style={labelStyle}>{lang === "id" ? "Kuota (opsional)" : "Quota (optional)"}</span>
            <input style={inputStyle} type="number" min="1" value={quota} onChange={(e) => setQuota(e.target.value)} placeholder={lang === "id" ? "tak terbatas" : "unlimited"} />
          </label>
        </div>
        <div className="row gap-2" style={{ justifyContent: "flex-end", marginTop: 4 }}>
          <Btn variant="ghost" type="button" onClick={onClose}>{lang === "id" ? "Batal" : "Cancel"}</Btn>
          <Btn variant="primary" type="submit" disabled={busy || !title.trim()}>{busy ? "…" : (lang === "id" ? "Simpan" : "Save")}</Btn>
        </div>
      </form>
    </Modal>
  )
}

// ── Lesson create/edit modal ──────────────────────────────────
function LessonFormModal({ programId, moduleId, lesson, onClose, onSaved }) {
  const { lang } = useApp()
  const isEdit = !!lesson
  const [title, setTitle] = React.useState(lesson?.title || "")
  const [type, setType] = React.useState(lesson?.type || "VIDEO")
  const [contentUrl, setContentUrl] = React.useState(lesson?.content_url || "")
  const [file, setFile] = React.useState(null)
  // Pelajaran yang sudah tersimpan tetapi unggahan dokumennya gagal: dicoba lagi
  // di sini, tanpa membuat pelajaran kedua.
  const [saved, setSaved] = React.useState(null)
  const [busy, setBusy] = React.useState(false)
  const [err, setErr] = React.useState(null)
  const up = useUpload()

  // Pelajaran yang dokumennya diunggah ke sini, bukan menunjuk ke alamat luar.
  // Nilai yang terbaca darinya adalah tautan bertanda tangan; menampilkannya di
  // kotak URL hanya mengundang orang menyuntingnya, dan mengirimkannya kembali
  // berarti menyimpan sesuatu yang berumur pendek.
  const berkasUnggahan = (lesson?.content_url || "").includes("/storage/")

  // Menutup saat unggahan berjalan akan memutus berkas di tengah jalan.
  function requestClose() {
    if (busy) return
    if (saved) onSaved(saved, {})
    else onClose()
  }

  async function submit(e) {
    e.preventDefault()
    setBusy(true); setErr(null)
    const kirimBerkas = type === "PDF" && !!file
    let tahap = "simpan"
    try {
      const payload = { title: title.trim(), type }
      // Dokumen unggahan dikelola lewat berkasnya sendiri, jadi kolom ini
      // sengaja tidak ikut dikirim bila ada berkas — kalau ikut, menyunting
      // judul saja akan menimpa alamat berkasnya.
      if (type !== "PDF") payload.content_url = null
      else if (!kirimBerkas && !berkasUnggahan) payload.content_url = contentUrl.trim() || null

      const target = saved || lesson
      const result = (target
        ? await updateLesson(programId, target.id, payload)
        : await createLesson(programId, moduleId, payload)) || target
      setSaved(result)

      if (kirimBerkas) {
        tahap = "unggah"
        await up.run(file, (o) => uploadLessonDoc(programId, result.id, file, o))
      }
      toast.success(lang === "id" ? "Pelajaran disimpan" : "Lesson saved")
      onSaved(result, { needsDoc: !isEdit && type === "PDF" && !kirimBerkas && !contentUrl.trim() })
    } catch (ex) {
      const pesan = isCanceled(ex)
        ? (lang === "id" ? "unggahan dibatalkan" : "the upload was cancelled")
        : (ex?.response?.data?.error || ex.message)
      setErr(tahap === "unggah"
        ? (lang === "id"
          ? `Pelajaran sudah tersimpan, tetapi dokumennya belum terunggah: ${pesan}. Tekan Simpan untuk mencoba lagi.`
          : `The lesson is saved, but the document was not uploaded: ${pesan}. Press Save to try again.`)
        : pesan)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal title={isEdit ? (lang === "id" ? "Edit Pelajaran" : "Edit Lesson") : (lang === "id" ? "Pelajaran Baru" : "New Lesson")} onClose={requestClose}>
      <form onSubmit={submit} className="col gap-3">
        {err && <div className="banner red"><Icon name="alert" size={14} />{err}</div>}
        <label className="col gap-1">
          <span style={labelStyle}>{lang === "id" ? "Judul" : "Title"}</span>
          <input style={inputStyle} value={title} onChange={(e) => setTitle(e.target.value)} required autoFocus />
        </label>
        <label className="col gap-1">
          <span style={labelStyle}>{lang === "id" ? "Tipe" : "Type"}</span>
          <select style={inputStyle} value={type} onChange={(e) => setType(e.target.value)} disabled={!!saved}>
            {["VIDEO", "PDF", "GAME"].map((t) => <option key={t} value={t}>{lessonTypeLabel(t, lang)}</option>)}
          </select>
        </label>
        {type === "PDF" && (
          <div className="col gap-1">
            <span style={labelStyle}>{lang === "id" ? "Dokumen (PDF)" : "Document (PDF)"}</span>
            {up.progress ? (
              <UploadProgress name={up.progress.name} loaded={up.progress.loaded} total={up.progress.total} onCancel={up.cancel} />
            ) : file ? (
              <div className="row gap-2" style={{ alignItems: "center", padding: "6px 8px", background: "var(--surface-2)", borderRadius: 6 }}>
                <Icon name="doc" size={13} color="var(--text-3)" />
                <span className="small" style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.name}</span>
                <span className="tiny muted">{fmtBytes(file.size)}</span>
                <Btn type="button" variant="ghost" size="sm" disabled={busy}
                  aria-label={lang === "id" ? "Lepas berkas" : "Remove file"}
                  icon={<Icon name="close" size={12} />} onClick={() => setFile(null)} />
              </div>
            ) : (
              <FileDropzone accept={LESSON_DOC_TYPES} maxBytes={DOC_MAX_BYTES} compact onFiles={(f) => setFile(f[0])} />
            )}
            {berkasUnggahan && !file && (
              <span className="tiny muted">
                {lang === "id"
                  ? "Dokumen sudah terpasang. Pilih berkas baru hanya jika ingin menggantinya."
                  : "A document is already attached. Choose a new file only to replace it."}
              </span>
            )}
            {file && (
              <span className="tiny muted">
                {lang === "id" ? "Berkas diunggah saat Anda menekan Simpan." : "The file is uploaded when you press Save."}
              </span>
            )}
            {!berkasUnggahan && !file && (
              <label className="col gap-1" style={{ marginTop: 6 }}>
                <span className="tiny muted">
                  {lang === "id" ? "Atau gunakan tautan PDF publik dari luar (opsional)" : "Or use an external public PDF link (optional)"}
                </span>
                <input style={inputStyle} type="url" value={contentUrl} onChange={(e) => setContentUrl(e.target.value)} placeholder="https://…/file.pdf" />
              </label>
            )}
          </div>
        )}
        {type === "VIDEO" && !isEdit && (
          <div className="tiny muted">{lang === "id" ? "Setelah dibuat, unggah file video dari daftar pelajaran." : "After creating, upload the video file from the lesson list."}</div>
        )}
        {type === "GAME" && !isEdit && (
          <div className="tiny muted">{lang === "id" ? "Setelah dibuat, atur tahap simulasi dan unggah build dari daftar pelajaran." : "After creating, configure simulation stages and upload the build from the lesson list."}</div>
        )}
        <div className="row gap-2" style={{ justifyContent: "flex-end", marginTop: 4 }}>
          <Btn variant="ghost" type="button" disabled={busy} onClick={requestClose}>{lang === "id" ? "Batal" : "Cancel"}</Btn>
          <Btn variant="primary" type="submit" disabled={busy || !title.trim()}>
            {busy ? (up.busy ? (lang === "id" ? "Mengunggah…" : "Uploading…") : "…") : (lang === "id" ? "Simpan" : "Save")}
          </Btn>
        </div>
      </form>
    </Modal>
  )
}

// ── Video upload manager (inline, for VIDEO lessons) ──────────
function VideoManager({ programId, lesson, onChanged }) {
  const { lang } = useApp()
  const [file, setFile] = React.useState(null)
  const [busy, setBusy] = React.useState(false)
  const fileRef = React.useRef(null)

  async function upload() {
    if (!file) return
    setBusy(true)
    try {
      await uploadVideoForLesson(programId, lesson.id, file, {})
      toast.success(lang === "id" ? "Video diunggah" : "Video uploaded")
      setFile(null); if (fileRef.current) fileRef.current.value = ""
      onChanged()
    } catch (ex) {
      toast.error(ex?.response?.data?.error || ex.message)
    } finally {
      setBusy(false)
    }
  }
  async function removeVideo() {
    if (!lesson.video) return
    if (!window.confirm(lang === "id" ? "Hapus video ini?" : "Delete this video?")) return
    try {
      await deleteVideo(programId, lesson.video.id)
      toast.success(lang === "id" ? "Video dihapus" : "Video deleted")
      onChanged()
    } catch (ex) {
      toast.error(ex?.response?.data?.error || ex.message)
    }
  }

  return (
    <div className="col gap-2" style={{ padding: "8px 0 2px" }}>
      {lesson.video ? (
        <div className="row gap-2" style={{ alignItems: "center" }}>
          <Badge tone="success" dot>{lang === "id" ? "Video terpasang" : "Video attached"}</Badge>
          <span className="tiny muted" style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{lesson.video.title}</span>
          <Btn variant="ghost" size="sm" icon={<Icon name="trash" size={12} />} onClick={removeVideo}>{lang === "id" ? "Hapus" : "Remove"}</Btn>
        </div>
      ) : (
        <span className="tiny muted">{lang === "id" ? "Belum ada video." : "No video yet."}</span>
      )}
      <div className="row gap-2" style={{ alignItems: "center" }}>
        <input ref={fileRef} type="file" accept="video/*" onChange={(e) => setFile(e.target.files?.[0] || null)} style={{ fontSize: 12, flex: 1 }} />
        <Btn variant="secondary" size="sm" disabled={!file || busy} onClick={upload}>{busy ? "…" : (lang === "id" ? (lesson.video ? "Ganti" : "Unggah") : (lesson.video ? "Replace" : "Upload"))}</Btn>
      </div>
    </div>
  )
}

// ── Dokumen pelajaran (inline, untuk pelajaran PDF) ───────────
//
// Kolom "Document URL" di formulir hanya menerima alamat publik dari luar.
// Berkas yang diunggah ke sini disajikan lewat tautan bertanda tangan yang
// dibuat ulang tiap kali dibaca — jadi ia tidak bisa ditempel ke kolom itu,
// dan tanpa panel ini pelajaran bertipe Document milik sendiri selalu tampil
// kosong di layar peserta.
function LessonDocManager({ programId, lesson, onChanged }) {
  const { lang } = useApp()
  const [replacing, setReplacing] = React.useState(false)
  const [confirmDelete, setConfirmDelete] = React.useState(false)
  const [deleting, setDeleting] = React.useState(false)

  // Berkas milik sendiri dikenali dari jalurnya; selain itu berarti tautan luar,
  // dan tautan luar tidak boleh ditawarkan untuk "dihapus" seolah berkas kita.
  const punyaBerkas = !!lesson.content_url && lesson.content_url.includes("/storage/")
  const tautanLuar = !!lesson.content_url && !punyaBerkas

  // Muatan kursus tidak lagi membawa tautan bertanda tangan untuk dokumen
  // pelajaran, jadi content_url di sini adalah jalur mentah yang akan ditolak
  // rute /storage. Tautannya diminta saat tombol ditekan.
  async function lihat() {
    try {
      const { url } = await getLessonDocUrl(programId, lesson.id)
      window.open(url, "_blank", "noopener,noreferrer")
    } catch (ex) {
      toast.error(ex?.response?.data?.error || ex.message)
    }
  }

  async function hapus() {
    setDeleting(true)
    try {
      await deleteLessonDoc(programId, lesson.id)
      toast.success(lang === "id" ? "Dokumen dihapus" : "Document deleted")
      setConfirmDelete(false)
      onChanged()
    } catch (ex) {
      toast.error(ex?.response?.data?.error || ex.message)
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="col gap-2" style={{ padding: "8px 0 10px" }}>
      {punyaBerkas ? (
        <div className="row gap-2" style={{ alignItems: "center", flexWrap: "wrap", padding: "6px 8px", background: "var(--surface-2)", borderRadius: 6 }}>
          <Icon name="doc" size={13} color="var(--text-3)" />
          <Badge tone="success" dot>{lang === "id" ? "Dokumen terpasang" : "Document attached"}</Badge>
          <span style={{ flex: 1 }} />
          <Btn variant="ghost" size="sm" icon={<Icon name="eye" size={12} />} onClick={lihat}>{lang === "id" ? "Lihat" : "View"}</Btn>
          <Btn variant="ghost" size="sm" icon={<Icon name="refresh" size={12} />} onClick={() => setReplacing((r) => !r)}>
            {replacing ? (lang === "id" ? "Tutup" : "Close") : (lang === "id" ? "Ganti" : "Replace")}
          </Btn>
          <Btn variant="ghost" size="sm" icon={<Icon name="trash" size={12} />} onClick={() => setConfirmDelete(true)}>{lang === "id" ? "Hapus" : "Delete"}</Btn>
        </div>
      ) : tautanLuar ? (
        <div className="row gap-2" style={{ alignItems: "center", padding: "6px 8px", background: "var(--surface-2)", borderRadius: 6 }}>
          <Icon name="external" size={13} color="var(--text-3)" />
          <span className="tiny muted" style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {lang === "id" ? "Tautan luar: " : "External link: "}{lesson.content_url}
          </span>
        </div>
      ) : (
        <div className="banner amber">
          <Icon name="alert" size={14} />
          {lang === "id" ? "Belum ada dokumen — peserta melihat halaman kosong." : "No document yet — participants see an empty page."}
        </div>
      )}

      {(!punyaBerkas || replacing) && (
        <DocUploader
          accept={LESSON_DOC_TYPES}
          maxBytes={DOC_MAX_BYTES}
          upload={(f, o) => uploadLessonDoc(programId, lesson.id, f, o)}
          onUploaded={() => { setReplacing(false); onChanged() }}
        />
      )}

      {confirmDelete && (
        <ConfirmModal
          title={lang === "id" ? "Hapus dokumen?" : "Delete document?"}
          message={lang === "id"
            ? `Dokumen pelajaran "${lesson.title}" akan dihapus, dan peserta tidak lagi melihatnya.`
            : `The document of "${lesson.title}" will be deleted and participants will no longer see it.`}
          confirmLabel={lang === "id" ? "Hapus" : "Delete"}
          busy={deleting}
          onConfirm={hapus}
          onClose={() => setConfirmDelete(false)}
        />
      )}
    </div>
  )
}

// ── Game scenario editor (inline panel, for GAME lessons) ─────
// stage_config is one JSON array field on GameScenario rather than a
// separate table of rows — edited as a batch
// (all stages, save once) instead of per-row API calls.
function GameManager({ lesson }) {
  const { lang } = useApp()
  const [scenario, setScenario] = React.useState(null)
  const [loading, setLoading] = React.useState(true)
  const [stages, setStages] = React.useState([])
  const [masteryThreshold, setMasteryThreshold] = React.useState(80)
  const [maxAttempts, setMaxAttempts] = React.useState("")
  // Keputusan trainer/admin: haruskah peserta LULUS agar pelajaran ini dianggap selesai?
  const [countsForCompletion, setCountsForCompletion] = React.useState(true)
  const [saving, setSaving] = React.useState(false)
  const [file, setFile] = React.useState(null)
  const [uploading, setUploading] = React.useState(false)
  const fileRef = React.useRef(null)

  const reload = React.useCallback(() => {
    setLoading(true)
    getGameScenarioAdmin(lesson.id)
      .then((s) => {
        setScenario(s)
        setStages(Array.isArray(s.stage_config) ? s.stage_config : [])
        setMasteryThreshold(s.mastery_threshold)
        setMaxAttempts(s.max_attempts ?? "")
        setCountsForCompletion(s.counts_for_completion !== false)
      })
      .catch((e) => { if (e?.response?.status === 404) setScenario(null); else toast.error(e?.response?.data?.error || e.message) })
      .finally(() => setLoading(false))
  }, [lesson.id])
  React.useEffect(() => { reload() }, [reload])

  async function createScenario() {
    try {
      // Phase 1 (MVP) default: a single Tactical Field Response stage —
      // see the plan doc. Trainers extend this to the full 5-stage
      // scenario via "Add stage" once later phases land.
      await upsertGameScenario(lesson.id, {
        title: lesson.title,
        engine: "GODOT",
        stage_config: [{
          stage_key: "boom_deployment",
          label: lang === "id" ? "Respons Taktis — Pemasangan Boom" : "Tactical Response — Boom Deployment",
          passing_score: 80,
          required: true,
        }],
        mastery_threshold: 80,
      })
      reload()
    } catch (e) { toast.error(e?.response?.data?.error || e.message) }
  }

  async function useTemplate() {
    try {
      await createGameFromTemplate(lesson.id, "oilspill-boom")
      toast.success(lang === "id" ? "Skenario dibuat dari templat" : "Scenario created from template")
      reload()
    } catch (e) { toast.error(e?.response?.data?.error || e.message) }
  }

  function updateStage(i, field, value) {
    setStages((prev) => prev.map((s, idx) => (idx === i ? { ...s, [field]: value } : s)))
  }
  function addStage() {
    setStages((prev) => [...prev, { stage_key: `stage_${prev.length + 1}`, label: "", passing_score: 80, required: true }])
  }
  function removeStage(i) {
    setStages((prev) => prev.filter((_, idx) => idx !== i))
  }

  async function saveConfig() {
    if (stages.length === 0) {
      toast.error(lang === "id" ? "Perlu minimal satu tahap." : "At least one stage is required.")
      return
    }
    if (stages.some((s) => !s.stage_key.trim() || !s.label.trim())) {
      toast.error(lang === "id" ? "Setiap tahap perlu kunci dan nama." : "Every stage needs a key and a label.")
      return
    }
    // Kolom nilai yang dikosongkan menjadi 0 lewat Number(""), dan 0 diterima
    // server sebagai ambang yang sah — gerbang kelulusan mati tanpa ada yang
    // memberi tahu. Ditangkap di sini selagi masih bisa diperbaiki.
    if (masteryThreshold === "" || stages.some((s) => s.passing_score === "" || s.passing_score == null)) {
      toast.error(lang === "id" ? "Nilai lulus tidak boleh kosong." : "Pass score cannot be empty.")
      return
    }
    setSaving(true)
    try {
      await updateGameScenario(scenario.id, {
        stage_config: stages,
        mastery_threshold: Number(masteryThreshold),
        max_attempts: maxAttempts === "" ? null : Number(maxAttempts),
        counts_for_completion: countsForCompletion,
      })
      toast.success(lang === "id" ? "Konfigurasi disimpan" : "Configuration saved")
      reload()
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message)
    } finally {
      setSaving(false)
    }
  }

  async function uploadBuild() {
    if (!file) return
    setUploading(true)
    try {
      await uploadGameBuild(scenario.id, file)
      toast.success(lang === "id" ? "Build diunggah" : "Build uploaded")
      setFile(null); if (fileRef.current) fileRef.current.value = ""
      reload()
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message)
    } finally {
      setUploading(false)
    }
  }

  if (loading) return <div className="tiny muted" style={{ padding: 8 }}>{lang === "id" ? "Memuat konfigurasi simulasi…" : "Loading simulation config…"}</div>
  if (!scenario) {
    return (
      <div className="row gap-2" style={{ padding: "8px 0", alignItems: "center", flexWrap: "wrap" }}>
        <span className="tiny muted" style={{ flex: 1, minWidth: 200 }}>{lang === "id" ? "Belum ada skenario simulasi untuk pelajaran ini." : "No simulation scenario for this lesson yet."}</span>
        <Btn variant="primary" size="sm" icon={<Icon name="plus" size={12} />} onClick={useTemplate}>
          {lang === "id" ? "Pakai templat: Tumpahan Minyak — Boom" : "Use template: Oil Spill — Boom"}
        </Btn>
        <Btn variant="secondary" size="sm" onClick={createScenario}>{lang === "id" ? "Skenario custom" : "Custom scenario"}</Btn>
      </div>
    )
  }

  return (
    <div className="col gap-3" style={{ padding: "8px 0 2px" }}>
      <div className="row gap-3" style={{ flexWrap: "wrap" }}>
        <label className="col gap-1">
          <span style={labelStyle}>{lang === "id" ? "Nilai lulus (%)" : "Pass score (%)"}</span>
          <input style={{ ...inputStyle, width: 110 }} type="number" min="0" max="100" value={masteryThreshold}
            onChange={(e) => setMasteryThreshold(e.target.value)} />
        </label>
        <label className="col gap-1">
          <span style={labelStyle}>{lang === "id" ? "Maks. percobaan" : "Max attempts"}</span>
          <input style={{ ...inputStyle, width: 170 }} type="number" min="1" value={maxAttempts}
            onChange={(e) => setMaxAttempts(e.target.value)} placeholder={lang === "id" ? "tak terbatas" : "unlimited"} />
        </label>
      </div>
      <label className="row gap-2 small" style={{ alignItems: "flex-start" }}>
        <input type="checkbox" checked={countsForCompletion} onChange={(e) => setCountsForCompletion(e.target.checked)} style={{ marginTop: 3 }} />
        <span>
          {lang === "id" ? "Peserta harus lulus agar pelajaran dianggap selesai" : "Participants must pass for the lesson to count as complete"}
          <span className="tiny muted" style={{ display: "block" }}>
            {lang === "id"
              ? "Nonaktifkan untuk menjadikannya latihan: pelajaran selesai begitu simulasi dikirim, berapa pun skornya. Tidak memengaruhi sertifikat."
              : "Turn off to make it practice-only: the lesson completes as soon as the simulation is submitted, whatever the score. Does not affect certificates."}
          </span>
        </span>
      </label>

      <div className="col gap-2">
        <span style={labelStyle}>{lang === "id" ? "Tahap" : "Stages"}</span>
        {stages.map((s, i) => (
          <div key={i} className="row gap-2" style={{ padding: "8px 10px", background: "var(--surface-2)", borderRadius: 6, alignItems: "center", flexWrap: "wrap" }}>
            <span className="mono tiny muted">{String(i + 1).padStart(2, "0")}</span>
            <input style={{ ...inputStyle, width: 140 }} value={s.stage_key} onChange={(e) => updateStage(i, "stage_key", e.target.value)} placeholder="stage_key" />
            <input style={{ ...inputStyle, flex: 1, minWidth: 160 }} value={s.label} onChange={(e) => updateStage(i, "label", e.target.value)} placeholder={lang === "id" ? "Nama tahap" : "Stage label"} />
            {/* Nilai mentah disimpan apa adanya — string kosong tetap kosong,
                bukan berubah jadi 0 diam-diam. saveConfig() yang menolaknya. */}
            <input style={{ ...inputStyle, width: 90 }} type="number" min="0" max="100" value={s.passing_score}
              onChange={(e) => updateStage(i, "passing_score", e.target.value === "" ? "" : Number(e.target.value))} />
            {scenario.scoring_mode === "SERVER" && (
              <input style={{ ...inputStyle, width: 80 }} type="number" min="1" title={lang === "id" ? "Bobot dalam skor akhir" : "Weight in final score"}
                value={s.weight ?? ""} placeholder={lang === "id" ? "bobot" : "weight"}
                onChange={(e) => updateStage(i, "weight", e.target.value === "" ? undefined : Number(e.target.value))} />
            )}
            <label className="row gap-1 tiny" style={{ alignItems: "center" }}>
              <input type="checkbox" checked={s.required !== false} onChange={(e) => updateStage(i, "required", e.target.checked)} />
              {lang === "id" ? "Wajib" : "Required"}
            </label>
            <Btn variant="ghost" size="sm" icon={<Icon name="trash" size={12} />} onClick={() => removeStage(i)} />
          </div>
        ))}
      </div>
      <div className="row gap-2">
        <Btn variant="secondary" size="sm" icon={<Icon name="plus" size={12} />} onClick={addStage}>{lang === "id" ? "Tambah tahap" : "Add stage"}</Btn>
        <Btn variant="primary" size="sm" disabled={saving} onClick={saveConfig}>{saving ? "…" : (lang === "id" ? "Simpan konfigurasi" : "Save configuration")}</Btn>
      </div>

      {scenario.build_path?.startsWith("_builtin/") ? (
        <div className="col gap-1" style={{ marginTop: 6, paddingTop: 10, borderTop: "1px dashed var(--border-subtle)" }}>
          <span style={labelStyle}>{lang === "id" ? "Simulasi bawaan platform" : "Built-in platform simulation"}</span>
          <Badge tone="success" dot>{lang === "id" ? "Build & penilaian disediakan platform" : "Build and scoring provided by the platform"}</Badge>
          <span className="tiny muted">{lang === "id" ? "Skor dihitung di server dari keputusan peserta; tidak perlu mengunggah build." : "The score is calculated on the server from the participant's decisions; no build upload needed."}</span>
        </div>
      ) : (
      <div className="col gap-2" style={{ marginTop: 6, paddingTop: 10, borderTop: "1px dashed var(--border-subtle)" }}>
        <span style={labelStyle}>{lang === "id" ? "Build WebGL (Godot)" : "WebGL build (Godot)"}</span>
        {scenario.build_version ? (
          <Badge tone="success" dot>{lang === "id" ? `Build terpasang · versi ${scenario.build_version}` : `Build attached · version ${scenario.build_version}`}</Badge>
        ) : (
          <span className="tiny muted">{lang === "id" ? "Belum ada build — peserta tidak bisa memulai simulasi." : "No build yet — participants can't start the simulation."}</span>
        )}
        <div className="row gap-2" style={{ alignItems: "center" }}>
          <input ref={fileRef} type="file" accept=".zip" onChange={(e) => setFile(e.target.files?.[0] || null)} style={{ fontSize: 12, flex: 1 }} />
          <Btn variant="secondary" size="sm" disabled={!file || uploading} onClick={uploadBuild}>
            {uploading ? "…" : (lang === "id" ? (scenario.build_version ? "Ganti" : "Unggah") : (scenario.build_version ? "Replace" : "Upload"))}
          </Btn>
        </div>
        <span className="tiny muted">{lang === "id" ? "File .zip hasil export WebGL Godot (index.html di root zip)." : "The .zip from Godot's WebGL export (index.html at the zip root)."}</span>
      </div>
      )}
    </div>
  )
}

// ── Module document manager (inline) ──────────────────────────
function fmtDocDate(d, lang) {
  if (!d) return ""
  try { return new Date(d).toLocaleDateString(lang === "id" ? "id-ID" : "en-US", { day: "numeric", month: "short", year: "numeric" }) }
  catch { return "" }
}

function DocManager({ programId, mod, onReload }) {
  const { lang } = useApp()
  const docs = mod.documents || []
  const [toDelete, setToDelete] = React.useState(null)
  const [deleting, setDeleting] = React.useState(false)

  async function lihat(d) {
    try {
      const { url } = await getModuleDocDownloadUrl(programId, d.id)
      window.open(url, "_blank", "noopener,noreferrer")
    } catch (ex) {
      toast.error(ex?.response?.data?.error || ex.message)
    }
  }
  async function removeDoc() {
    setDeleting(true)
    try {
      await deleteModuleDoc(programId, toDelete.id)
      toast.success(lang === "id" ? "Dokumen dihapus" : "Document deleted")
      setToDelete(null)
      onReload()
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message)
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="col gap-2" style={{ marginTop: 12, paddingTop: 10, borderTop: "1px dashed var(--border-subtle)" }}>
      <span style={labelStyle}>{lang === "id" ? "Dokumen modul" : "Module documents"}{docs.length > 0 ? ` (${docs.length})` : ""}</span>
      {docs.map((d) => {
        const meta = [d.file_type, fmtBytes(d.size_bytes), fmtDocDate(d.created_at, lang)].filter(Boolean).join(" · ")
        return (
          <div key={d.id} className="row gap-2" style={{ alignItems: "center", padding: "6px 8px", background: "var(--surface-2)", borderRadius: 6 }}>
            <Icon name="doc" size={13} color="var(--text-3)" />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="small" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={d.name}>{d.name}</div>
              {meta && <div className="tiny muted">{meta}</div>}
            </div>
            <Btn variant="ghost" size="sm" icon={<Icon name="eye" size={12} />} onClick={() => lihat(d)}>{lang === "id" ? "Lihat" : "View"}</Btn>
            <Btn variant="ghost" size="sm" icon={<Icon name="trash" size={12} />} onClick={() => setToDelete(d)}>{lang === "id" ? "Hapus" : "Delete"}</Btn>
          </div>
        )
      })}
      {docs.length === 0 && <span className="tiny muted">{lang === "id" ? "Belum ada dokumen." : "No documents yet."}</span>}
      <DocUploader
        multiple
        accept={MODULE_DOC_TYPES}
        maxBytes={DOC_MAX_BYTES}
        upload={(f, o) => uploadModuleDoc(programId, mod.id, f, undefined, o)}
        onUploaded={onReload}
      />
      {toDelete && (
        <ConfirmModal
          title={lang === "id" ? "Hapus dokumen?" : "Delete document?"}
          message={lang === "id" ? `"${toDelete.name}" akan dihapus dari modul ini.` : `"${toDelete.name}" will be removed from this module.`}
          confirmLabel={lang === "id" ? "Hapus" : "Delete"}
          busy={deleting}
          onConfirm={removeDoc}
          onClose={() => setToDelete(null)}
        />
      )}
    </div>
  )
}

// ── Dokumen referensi kursus: daftar yang dinamai admin ───────
//
// Dulu dua slot tetap bernama "IMO OPRC Manual Book/WorkBook", yang tidak cocok untuk
// lembaga dengan program lain. Kini admin menambah dokumen sebanyak yang diperlukan dan
// menamainya sendiri; semuanya tersedia untuk seluruh peserta kursus ini.
function CourseDocManager({ programId, course, onReload }) {
  const { lang } = useApp()
  const isMobile = useIsMobile()
  const docs = course.ref_docs || []
  const [renamingId, setRenamingId] = React.useState(null)
  const [draft, setDraft] = React.useState("")
  const [toDelete, setToDelete] = React.useState(null)
  const [deleting, setDeleting] = React.useState(false)

  // Tautan segar tiap kali dibuka: tautan bertanda tangan berumur pendek.
  async function lihat(d) {
    try {
      const { url } = await getCourseDocDownloadUrl(programId, d.id)
      window.open(url, "_blank", "noopener,noreferrer")
    } catch (ex) {
      toast.error(ex?.response?.data?.error || ex.message)
    }
  }

  function mulaiGanti(d) {
    setDraft(d.name)
    setRenamingId(d.id)
  }
  async function simpanNama(d) {
    setRenamingId(null)
    const nama = draft.trim()
    if (!nama || nama === d.name) return
    try { await renameCourseDoc(programId, d.id, nama); onReload() }
    catch (ex) { toast.error(ex?.response?.data?.error || ex.message) }
  }

  async function hapus() {
    setDeleting(true)
    try {
      await deleteCourseDoc(programId, toDelete.id)
      toast.success(lang === "id" ? "Dokumen dihapus" : "Document deleted")
      setToDelete(null)
      onReload()
    } catch (ex) {
      toast.error(ex?.response?.data?.error || ex.message)
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="col gap-2">
      {docs.length === 0 && (
        <span className="tiny muted">
          {lang === "id"
            ? "Belum ada dokumen referensi. Tambahkan buku panduan, buku kerja, atau materi rujukan lain yang berlaku untuk seluruh kursus ini."
            : "No reference documents yet. Add a manual, workbook, or other reference material that applies to the whole course."}
        </span>
      )}
      {docs.map((d) => {
        const meta = [d.file_type, fmtBytes(d.size_bytes), fmtDocDate(d.created_at, lang)].filter(Boolean).join(" · ")
        return (
          <div key={d.id} className="row gap-2" style={{ alignItems: "center", flexWrap: "wrap", padding: "6px 8px", background: "var(--surface-2)", borderRadius: 6 }}>
            <Icon name="doc" size={13} color="var(--text-3)" />
            <div style={{ flex: "1 1 160px", minWidth: 0 }}>
              {renamingId === d.id ? (
                <input autoFocus maxLength={200} style={{ ...inputStyle, height: 28 }} value={draft}
                  aria-label={lang === "id" ? "Nama dokumen" : "Document name"}
                  onChange={(e) => setDraft(e.target.value)} onBlur={() => simpanNama(d)}
                  onKeyDown={(e) => { if (e.key === "Enter") simpanNama(d); if (e.key === "Escape") setRenamingId(null) }} />
              ) : (
                <div className="small" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "text" }}
                  title={d.name} onClick={() => mulaiGanti(d)}>{d.name}</div>
              )}
              {meta && <div className="tiny muted">{meta}</div>}
            </div>
            <div className="row gap-2" style={{ alignItems: "center", marginLeft: "auto", flexWrap: "wrap" }}>
              <Btn variant="ghost" size="sm" icon={<Icon name="eye" size={12} />} onClick={() => lihat(d)}
                title={lang === "id" ? "Lihat dokumen" : "View document"} aria-label={lang === "id" ? `Lihat ${d.name}` : `View ${d.name}`}>
                {isMobile ? null : (lang === "id" ? "Lihat" : "View")}
              </Btn>
              <Btn variant="ghost" size="sm" icon={<Icon name="edit" size={12} />} onClick={() => mulaiGanti(d)}
                title={lang === "id" ? "Ubah nama" : "Rename"} aria-label={lang === "id" ? `Ubah nama ${d.name}` : `Rename ${d.name}`}>
                {isMobile ? null : (lang === "id" ? "Ubah nama" : "Rename")}
              </Btn>
              <Btn variant="ghost" size="sm" icon={<Icon name="trash" size={12} />} onClick={() => setToDelete(d)}
                title={lang === "id" ? "Hapus dokumen" : "Delete document"} aria-label={lang === "id" ? `Hapus ${d.name}` : `Delete ${d.name}`}>
                {isMobile ? null : (lang === "id" ? "Hapus" : "Delete")}
              </Btn>
            </div>
          </div>
        )
      })}
      <DocUploader
        multiple
        accept={MODULE_DOC_TYPES}
        maxBytes={COURSE_DOC_MAX_BYTES}
        upload={(f, o) => uploadCourseDoc(programId, course.id, f, undefined, o)}
        onUploaded={onReload}
      />
      {toDelete && (
        <ConfirmModal
          title={lang === "id" ? "Hapus dokumen?" : "Delete document?"}
          message={lang === "id"
            ? `"${toDelete.name}" akan dihapus, dan peserta tidak lagi melihatnya.`
            : `"${toDelete.name}" will be deleted and participants will no longer see it.`}
          confirmLabel={lang === "id" ? "Hapus" : "Delete"}
          busy={deleting}
          onConfirm={hapus}
          onClose={() => setToDelete(null)}
        />
      )}
    </div>
  )
}

// ── Module block (with its lessons) ───────────────────────────
function ModuleBlock({ programId, mod, index, total, moving, onMove, onReload }) {
  const { lang } = useApp()
  const isMobile = useIsMobile()
  const [open, setOpen] = React.useState(true)
  const [editingTitle, setEditingTitle] = React.useState(false)
  const [titleDraft, setTitleDraft] = React.useState(mod.title)
  const [lessonModal, setLessonModal] = React.useState(null) // {} for new, lesson obj for edit
  const [expandedLesson, setExpandedLesson] = React.useState(null)
  const [markingLesson, setMarkingLesson] = React.useState(null)
  const [confirmDelete, setConfirmDelete] = React.useState(false)
  const [deleting, setDeleting] = React.useState(false)
  const [editingCode, setEditingCode] = React.useState(false)
  const [codeDraft, setCodeDraft] = React.useState("")

  // Menandai pelajaran selesai untuk seluruh kelas.
  //
  // Pelajaran hanya menjadi "selesai" kalau peserta menekan tombolnya sendiri
  // (atau lulus kuisnya). Menonton video sampai habis tidak menandainya. Dan
  // posttest baru terbuka setelah SEMUA pelajaran selesai — jadi satu peserta
  // yang lupa menekan, kehilangan sertifikatnya tanpa ada galat yang muncul.
  // Setelah materi dibahas bersama di kelas, ini menutup celah itu sekaligus.
  async function markAllComplete(l) {
    const tanya = lang === "id"
      ? `Tandai "${l.title}" selesai untuk SEMUA peserta program ini?\n\nIni memengaruhi kelayakan sertifikat mereka.`
      : `Mark "${l.title}" complete for ALL participants in this program?\n\nThis affects their certificate eligibility.`
    if (!window.confirm(tanya)) return
    setMarkingLesson(l.id)
    try {
      const hasil = await markLessonCompleteForAll(programId, l.id, true)
      toast.success(lang === "id"
        ? `Ditandai selesai untuk ${hasil.affected} peserta`
        : `Marked complete for ${hasil.affected} participants`)
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message)
    } finally {
      setMarkingLesson(null)
    }
  }

  async function saveTitle() {
    const t = titleDraft.trim()
    setEditingTitle(false)
    if (!t || t === mod.title) { setTitleDraft(mod.title); return }
    try { await updateModule(programId, mod.id, { title: t }); onReload() }
    catch (e) { toast.error(e?.response?.data?.error || e.message); setTitleDraft(mod.title) }
  }
  // Label modul. Tiga keadaan yang berbeda (lihat lib/modLabel.js): kolom
  // dikosongkan = kembali ke otomatis (null); "Tanpa label" = "" (disembunyikan
  // dengan sengaja); teks = kustom. Mengosongkan kolom TIDAK menyembunyikan label,
  // sebab itulah yang paling mungkin dimaksud orang yang menghapus isinya.
  function startEditCode() {
    setCodeDraft(mod.code ?? "")
    setEditingCode(true)
  }
  async function saveCode(next) {
    setEditingCode(false)
    const value = next === null ? null : next.trim()
    const sekarang = mod.code ?? null
    if (value === sekarang) return
    try { await updateModule(programId, mod.id, { code: value }); onReload() }
    catch (e) { toast.error(e?.response?.data?.error || e.message) }
  }
  const labelSekarang = modLabel(mod, index)

  async function removeModule() {
    setDeleting(true)
    try {
      await deleteModule(programId, mod.id)
      toast.success(lang === "id" ? "Modul dihapus" : "Module deleted")
      setConfirmDelete(false)
      onReload()
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message)
    } finally {
      setDeleting(false)
    }
  }

  // Yang ikut terhapus disebut satu per satu: progres peserta menempel pada
  // pelajaran (dihapus bersamanya), jadi "beserta semua pelajarannya" saja
  // terdengar lebih ringan daripada akibat yang sebenarnya.
  const jumlahPelajaran = mod.lessons.length
  const jumlahDokumen = (mod.documents || []).length
  const pesanHapusModul = jumlahPelajaran === 0 && jumlahDokumen === 0
    ? (lang === "id"
      ? `Modul "${mod.title}" akan dihapus. Tindakan ini tidak bisa dibatalkan.`
      : `Module "${mod.title}" will be deleted. This cannot be undone.`)
    : (lang === "id"
      ? `Modul "${mod.title}" akan dihapus bersama ${jumlahPelajaran} pelajaran${jumlahDokumen ? ` dan ${jumlahDokumen} dokumen modul` : ""}.${jumlahPelajaran ? " Progres peserta pada pelajaran itu ikut hilang." : ""} Tindakan ini tidak bisa dibatalkan.`
      : `Module "${mod.title}" will be deleted along with ${jumlahPelajaran} lesson${jumlahPelajaran === 1 ? "" : "s"}${jumlahDokumen ? ` and ${jumlahDokumen} module document${jumlahDokumen === 1 ? "" : "s"}` : ""}.${jumlahPelajaran ? " Participants' progress on those lessons is lost too." : ""} This cannot be undone.`)
  async function removeLesson(l) {
    if (!window.confirm(lang === "id" ? `Hapus pelajaran "${l.title}"?` : `Delete lesson "${l.title}"?`)) return
    try { await deleteLesson(programId, l.id); onReload() }
    catch (e) { toast.error(e?.response?.data?.error || e.message) }
  }

  return (
    <div style={{ border: "1px solid var(--border-subtle)", borderRadius: 10, marginBottom: 12, overflow: "hidden" }}>
      <div className="row gap-2" style={{ padding: "10px 12px", background: "var(--surface-2)", alignItems: "center", flexWrap: "wrap" }}>
        <button onClick={() => setOpen((o) => !o)} style={{ background: "transparent", border: 0, cursor: "pointer", display: "grid", placeItems: "center" }}>
          <Icon name="chevron_down" size={14} style={{ transform: open ? "rotate(0deg)" : "rotate(-90deg)", transition: "transform .2s" }} />
        </button>
        {editingCode ? (
          <span className="row gap-1" style={{ alignItems: "center", flexWrap: "wrap" }}>
            <input autoFocus maxLength={12} style={{ ...inputStyle, width: 96, height: 28 }} value={codeDraft}
              placeholder={`M${index + 1}`} aria-label={lang === "id" ? "Label modul" : "Module label"}
              onChange={(e) => setCodeDraft(e.target.value)}
              onBlur={() => saveCode(codeDraft.trim() === "" ? null : codeDraft)}
              onKeyDown={(e) => {
                if (e.key === "Enter") saveCode(codeDraft.trim() === "" ? null : codeDraft)
                if (e.key === "Escape") setEditingCode(false)
              }} />
            {/* onMouseDown dicegah agar kolom tidak kehilangan fokus (dan menyimpan
                dulu) sebelum klik tombol ini terbaca. */}
            <Btn variant="ghost" size="sm" type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => saveCode(null)}
              title={lang === "id" ? `Kembali ke label otomatis (M${index + 1})` : `Back to the automatic label (M${index + 1})`}>
              {lang === "id" ? "Otomatis" : "Automatic"}
            </Btn>
            <Btn variant="ghost" size="sm" type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => saveCode("")}
              title={lang === "id" ? "Sembunyikan label modul ini" : "Hide this module's label"}>
              {lang === "id" ? "Tanpa label" : "No label"}
            </Btn>
          </span>
        ) : (
          <button type="button" className="mono tiny muted" onClick={startEditCode}
            title={lang === "id" ? "Ubah label modul" : "Edit module label"}
            aria-label={lang === "id" ? `Ubah label modul (saat ini ${labelSekarang || "tanpa label"})` : `Edit module label (currently ${labelSekarang || "no label"})`}
            style={{ background: "transparent", border: 0, padding: "0 2px", cursor: "pointer", textDecoration: "underline dotted", textUnderlineOffset: 3, fontStyle: labelSekarang ? "normal" : "italic" }}>
            {labelSekarang || (lang === "id" ? "tanpa label" : "no label")}
          </button>
        )}
        {editingTitle ? (
          <input autoFocus style={{ ...inputStyle, flex: 1, height: 30 }} value={titleDraft}
            onChange={(e) => setTitleDraft(e.target.value)} onBlur={saveTitle}
            onKeyDown={(e) => { if (e.key === "Enter") saveTitle(); if (e.key === "Escape") { setEditingTitle(false); setTitleDraft(mod.title) } }} />
        ) : (
          <span style={{ flex: "1 1 120px", fontWeight: 600, fontSize: 13, cursor: "text" }} onClick={() => setEditingTitle(true)}>{mod.title}</span>
        )}
        <Badge tone="outline">{mod.lessons.length} {lang === "id" ? "pelajaran" : "lessons"}</Badge>
        <Btn variant="ghost" size="sm" icon={<Icon name="chevron_up" size={12} />} disabled={index === 0 || moving} onClick={() => onMove(-1)}
          title={lang === "id" ? "Naikkan modul" : "Move module up"} aria-label={lang === "id" ? "Naikkan modul" : "Move module up"} />
        <Btn variant="ghost" size="sm" icon={<Icon name="chevron_down" size={12} />} disabled={index === total - 1 || moving} onClick={() => onMove(1)}
          title={lang === "id" ? "Turunkan modul" : "Move module down"} aria-label={lang === "id" ? "Turunkan modul" : "Move module down"} />
        {/* Berlabel, bukan hanya ikon: pensil dan tempat sampah tanpa teks mudah
            terlewat, dan orang jadi mengira modul tidak bisa disunting/dihapus.
            Di layar sempit tinggal ikon, dengan nama aksesibel tetap ada. */}
        <Btn variant="ghost" size="sm" icon={<Icon name="edit" size={12} />} onClick={() => setEditingTitle(true)}
          title={lang === "id" ? "Ubah judul modul" : "Rename module"} aria-label={lang === "id" ? "Ubah judul modul" : "Rename module"}>
          {isMobile ? null : (lang === "id" ? "Ubah" : "Rename")}
        </Btn>
        <Btn variant="ghost" size="sm" icon={<Icon name="trash" size={12} />} onClick={() => setConfirmDelete(true)}
          title={lang === "id" ? "Hapus modul" : "Delete module"} aria-label={lang === "id" ? "Hapus modul" : "Delete module"}>
          {isMobile ? null : (lang === "id" ? "Hapus" : "Delete")}
        </Btn>
      </div>

      {open && (
        <div style={{ padding: "8px 12px 12px" }}>
          {mod.lessons.map((l) => {
            const meta = LESSON_META[l.type] || LESSON_META.VIDEO
            const isExp = expandedLesson === l.id
            const canExpand = l.type === "VIDEO" || l.type === "PDF" || l.type === "GAME"
            return (
              <div key={l.id} style={{ borderBottom: "1px solid var(--border-subtle)" }}>
                <div className="row gap-2" style={{ padding: "8px 4px", alignItems: "center", flexWrap: "wrap" }}>
                  <span style={{ width: 26, height: 26, borderRadius: 6, background: meta.soft, color: meta.color, display: "grid", placeItems: "center", flexShrink: 0 }}>
                    <Icon name={meta.icon} size={13} color={meta.color} />
                  </span>
                  <div style={{ flex: "1 1 140px", minWidth: 0 }}>
                    <div className="small" style={{ fontWeight: 500 }}>{l.title}</div>
                    <div className="tiny muted">{lessonTypeLabel(l.type, lang)}{l.type === "VIDEO" && l.video ? " · ✓" : ""}{l.type === "PDF" && l.content_url ? " · ✓" : ""}{l.type === "PDF" && !l.content_url && <span style={{ color: "var(--amber-ink)" }}>{" · "}{lang === "id" ? "belum ada dokumen" : "no document yet"}</span>}</div>
                  </div>
                  <div className="row gap-2" style={{ alignItems: "center", marginLeft: "auto", flexWrap: "wrap" }}>
                    {canExpand && (
                      <Btn variant="ghost" size="sm" onClick={() => setExpandedLesson(isExp ? null : l.id)}>
                        {l.type === "VIDEO"
                          ? "Video"
                          : l.type === "PDF"
                            ? (lang === "id" ? "Dokumen" : "Document")
                            : (lang === "id" ? "Simulasi" : "Simulation")} <Icon name={isExp ? "chevron_up" : "chevron_down"} size={12} />
                      </Btn>
                    )}
                    {/* Diberi label, bukan hanya ikon: di sebelah tombol hapus,
                        ikon centang sendirian terlalu mudah disalahartikan. */}
                    <Btn
                      variant="ghost"
                      size="sm"
                      disabled={markingLesson != null}
                      title={lang === "id" ? "Tandai pelajaran ini selesai untuk semua peserta" : "Mark this lesson complete for all participants"}
                      icon={<Icon name="check" size={12} />}
                      onClick={() => markAllComplete(l)}
                    >
                      {markingLesson === l.id
                        ? "…"
                        : (lang === "id" ? "Semua selesai" : "All done")}
                    </Btn>
                    <Btn variant="ghost" size="sm" icon={<Icon name="edit" size={12} />} onClick={() => setLessonModal(l)} />
                    <Btn variant="ghost" size="sm" icon={<Icon name="trash" size={12} />} onClick={() => removeLesson(l)} />
                  </div>
                </div>
                {isExp && l.type === "VIDEO" && <VideoManager programId={programId} lesson={l} onChanged={onReload} />}
                {isExp && l.type === "PDF" && <LessonDocManager programId={programId} lesson={l} onChanged={onReload} />}
                {isExp && l.type === "GAME" && <GameManager lesson={l} />}
              </div>
            )
          })}
          {mod.lessons.length === 0 && <div className="tiny muted" style={{ padding: "6px 4px" }}>{lang === "id" ? "Belum ada pelajaran." : "No lessons yet."}</div>}
          <div style={{ marginTop: 10 }}>
            <Btn variant="secondary" size="sm" icon={<Icon name="plus" size={12} />} onClick={() => setLessonModal({})}>{lang === "id" ? "Tambah pelajaran" : "Add lesson"}</Btn>
          </div>
          <DocManager programId={programId} mod={mod} onReload={onReload} />
        </div>
      )}

      {confirmDelete && (
        <ConfirmModal
          title={lang === "id" ? "Hapus modul?" : "Delete module?"}
          message={pesanHapusModul}
          confirmLabel={lang === "id" ? "Hapus modul" : "Delete module"}
          busy={deleting}
          onConfirm={removeModule}
          onClose={() => setConfirmDelete(false)}
        />
      )}

      {lessonModal && (
        <LessonFormModal programId={programId} moduleId={mod.id} lesson={lessonModal.id ? lessonModal : null}
          onClose={() => setLessonModal(null)}
          onSaved={(saved, meta) => {
            setLessonModal(null)
            // Pelajaran Document baru tanpa berkas: langsung buka panel unggahnya.
            if (meta?.needsDoc && saved?.id) setExpandedLesson(saved.id)
            onReload()
          }} />
      )}
    </div>
  )
}

// ── Main screen ───────────────────────────────────────────────
export default function CourseManageScreen() {
  const { lang, currentProgramId, currentProgram } = useApp()
  const orgName = useOrgName()
  const isMobile = useIsMobile()
  const [courses, setCourses] = React.useState([])
  const [selectedId, setSelectedId] = React.useState(null)
  const [detail, setDetail] = React.useState(null)
  const [loadingDetail, setLoadingDetail] = React.useState(false)
  const [courseModal, setCourseModal] = React.useState(null) // {} new | course edit
  const [addingModule, setAddingModule] = React.useState(false)
  const [reordering, setReordering] = React.useState(false)
  const [newModuleTitle, setNewModuleTitle] = React.useState("")

  const loadCourses = React.useCallback(() => {
    if (!currentProgramId) return
    listCourses(currentProgramId).then((list) => {
      setCourses(list)
      setSelectedId((prev) => prev || list[0]?.id || null)
    }).catch((e) => toast.error(e?.response?.data?.error || e.message))
  }, [currentProgramId])
  React.useEffect(() => { loadCourses() }, [loadCourses])

  const loadDetail = React.useCallback(() => {
    if (!currentProgramId || !selectedId) { setDetail(null); return }
    setLoadingDetail(true)
    getCourse(currentProgramId, selectedId)
      .then(setDetail)
      .catch((e) => toast.error(e?.response?.data?.error || e.message))
      .finally(() => setLoadingDetail(false))
  }, [currentProgramId, selectedId])
  React.useEffect(() => { loadDetail() }, [loadDetail])

  async function removeCourse(c) {
    if (!window.confirm(lang === "id" ? `Hapus kursus "${c.title}" beserta seluruh modul & pelajaran?` : `Delete course "${c.title}" with all its modules & lessons?`)) return
    try {
      await deleteCourse(currentProgramId, c.id)
      toast.success(lang === "id" ? "Kursus dihapus" : "Course deleted")
      if (selectedId === c.id) setSelectedId(null)
      loadCourses()
    } catch (e) { toast.error(e?.response?.data?.error || e.message) }
  }
  // Menggeser satu modul = menulis ulang urutan SELURUH daftar (satu transaksi di
  // server). Bila daftar di server sudah berbeda dari yang tampil di sini —
  // admin lain menambah atau menghapus modul — server menolak dengan 400, dan
  // daftar dimuat ulang supaya orang melihat keadaan sebenarnya sebelum mencoba lagi.
  async function moveModule(index, delta) {
    const mods = detail?.modules || []
    const to = index + delta
    if (reordering || to < 0 || to >= mods.length) return
    const ids = mods.map((m) => m.id)
    ;[ids[index], ids[to]] = [ids[to], ids[index]]
    setReordering(true)
    try {
      await reorderModules(currentProgramId, detail.id, ids)
    } catch (e) {
      toast.error(e?.response?.status === 400
        ? (lang === "id" ? "Daftar modul sudah berubah di tempat lain. Daftar dimuat ulang — coba lagi." : "The module list changed elsewhere. It has been reloaded — try again.")
        : (e?.response?.data?.error || e.message))
    } finally {
      loadDetail()
      setReordering(false)
    }
  }
  async function addModule() {
    const t = newModuleTitle.trim()
    if (!t || !detail) return
    try {
      await createModule(currentProgramId, detail.id, { title: t })
      setNewModuleTitle(""); setAddingModule(false)
      loadDetail()
    } catch (e) { toast.error(e?.response?.data?.error || e.message) }
  }

  if (!currentProgramId) {
    return <div className="main-inner"><p className="muted">{lang === "id" ? "Pilih program dulu." : "Select a program first."}</p></div>
  }

  return (
    <div className="main-inner" style={{ maxWidth: "none" }}>
      <div className="page-header" style={{ marginBottom: 16 }}>
        <div className="page-header-text">
          <div className="eyebrow">{(currentProgram?.code || orgName).toUpperCase()}</div>
          <h1 className="h1" style={{ marginTop: 6 }}>{lang === "id" ? "Kelola Kursus" : "Manage Courses"}</h1>
          <div className="muted mt-1" style={{ fontSize: 13 }}>{lang === "id" ? "Susun kursus, modul, dan pelajaran (video, dokumen, simulasi)." : "Author courses, modules, and lessons (video, document, simulation)."}</div>
        </div>
        <div className="page-header-actions">
          <Btn variant="primary" icon={<Icon name="plus" size={14} />} onClick={() => setCourseModal({})}>{lang === "id" ? "Kursus baru" : "New course"}</Btn>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "280px minmax(0, 1fr)", gap: 20, alignItems: "flex-start" }}>
        {/* Course list */}
        <Card noPad>
          <div className="card-header" style={{ padding: "12px 14px" }}>
            <div className="h3">{lang === "id" ? "Kursus" : "Courses"}</div>
          </div>
          <div style={{ padding: 8 }}>
            {courses.length === 0 && <div className="tiny muted" style={{ padding: 8 }}>{lang === "id" ? "Belum ada kursus." : "No courses yet."}</div>}
            {courses.map((c) => (
              <button key={c.id} onClick={() => setSelectedId(c.id)}
                style={{ width: "100%", textAlign: "left", padding: "10px 12px", borderRadius: 8, border: "none", marginBottom: 2,
                  background: selectedId === c.id ? "var(--program-color-soft)" : "transparent", cursor: "pointer" }}>
                <div className="row gap-2" style={{ alignItems: "center" }}>
                  <span style={{ flex: 1, fontWeight: selectedId === c.id ? 600 : 500, fontSize: 13, color: selectedId === c.id ? "var(--program-color-text)" : "var(--text)" }}>{c.title}</span>
                  <Badge tone={STATUS_TONE[c.status] || "default"}>{statusLabel(c.status, lang)}</Badge>
                </div>
                <div className="tiny muted" style={{ marginTop: 3 }}>{c._count?.modules ?? 0} {lang === "id" ? "modul" : "modules"}</div>
              </button>
            ))}
          </div>
        </Card>

        {/* Selected course management */}
        {!detail ? (
          <Card><div className="empty" style={{ padding: 50 }}><Icon name="book" size={32} color="var(--text-4)" /><div className="muted mt-2">{loadingDetail ? (lang === "id" ? "Memuat…" : "Loading…") : (lang === "id" ? "Pilih atau buat kursus." : "Select or create a course.")}</div></div></Card>
        ) : (
          <div className="col gap-4">
            <Card>
              <div className="row gap-2" style={{ alignItems: "flex-start" }}>
                <div style={{ flex: 1 }}>
                  <div className="row gap-2" style={{ alignItems: "center" }}>
                    <h2 className="h2" style={{ margin: 0 }}>{detail.title}</h2>
                    <Badge tone={STATUS_TONE[detail.status] || "default"}>{statusLabel(detail.status, lang)}</Badge>
                  </div>
                  {detail.description && <div className="muted mt-1" style={{ fontSize: 13 }}>{detail.description}</div>}
                </div>
                <Btn variant="secondary" size="sm" icon={<Icon name="edit" size={12} />} onClick={() => setCourseModal(detail)}>{lang === "id" ? "Edit" : "Edit"}</Btn>
                <Btn variant="ghost" size="sm" icon={<Icon name="trash" size={12} />} onClick={() => removeCourse(detail)}>{lang === "id" ? "Hapus" : "Delete"}</Btn>
              </div>
            </Card>

            <Card title={lang === "id" ? "Dokumen Referensi" : "Reference Documents"}
              eyebrow={lang === "id" ? "Tersedia untuk semua peserta kursus ini" : "Available to all participants of this course"}>
              <CourseDocManager programId={currentProgramId} course={detail} onReload={loadDetail} />
            </Card>

            <Card title={lang === "id" ? "Modul & Pelajaran" : "Modules & Lessons"}
              action={<Btn variant="secondary" size="sm" icon={<Icon name="plus" size={12} />} onClick={() => setAddingModule(true)}>{lang === "id" ? "Tambah modul" : "Add module"}</Btn>}>
              {addingModule && (
                <div className="row gap-2" style={{ marginBottom: 12 }}>
                  <input autoFocus style={{ ...inputStyle, flex: 1 }} value={newModuleTitle} onChange={(e) => setNewModuleTitle(e.target.value)}
                    placeholder={lang === "id" ? "Judul modul" : "Module title"}
                    onKeyDown={(e) => { if (e.key === "Enter") addModule(); if (e.key === "Escape") { setAddingModule(false); setNewModuleTitle("") } }} />
                  <Btn variant="primary" size="sm" onClick={addModule} disabled={!newModuleTitle.trim()}>{lang === "id" ? "Simpan" : "Save"}</Btn>
                  <Btn variant="ghost" size="sm" onClick={() => { setAddingModule(false); setNewModuleTitle("") }}>{lang === "id" ? "Batal" : "Cancel"}</Btn>
                </div>
              )}
              {(detail.modules || []).map((m, i) => (
                <ModuleBlock key={m.id} programId={currentProgramId} mod={m} index={i} total={(detail.modules || []).length}
                  moving={reordering} onMove={(delta) => moveModule(i, delta)} onReload={loadDetail} />
              ))}
              {(detail.modules || []).length === 0 && !addingModule && (
                <div className="empty" style={{ padding: 30 }}><Icon name="layers" size={26} color="var(--text-4)" /><div className="muted mt-2">{lang === "id" ? "Belum ada modul. Tambahkan modul pertama." : "No modules yet. Add the first module."}</div></div>
              )}
            </Card>
          </div>
        )}
      </div>

      {courseModal && (
        <CourseFormModal programId={currentProgramId} course={courseModal.id ? courseModal : null}
          onClose={() => setCourseModal(null)}
          onSaved={() => { setCourseModal(null); loadCourses(); if (courseModal.id) loadDetail() }} />
      )}
    </div>
  )
}
